import type { BulleEnrichie } from '../partage/api/types';
import { CarreDIdee, ChevronDeLEtape, PastilleJamaisOuverte, PastilleEtat } from '../apparence';
import { gestesDuToucher, useDeuxGestes } from '../partage/deuxGestes';
import { gestesDeLaLigneAuPouce } from './ligneAuPouce';
import { titreDuRond } from './rondQuiCoche';
import {
  auMilieu,
  DEPLIER_SES_TACHES,
  GESTES_D_UNE_ETAPE_AU_POUCE,
  GESTES_D_UNE_TACHE_AU_POUCE,
  GESTES_DE_LA_LISTE,
  ligneDeProprietes,
  MOTS_ETAT,
  REPLIER_SES_TACHES,
  RIEN_DEDANS,
} from '../partage/vocabulaire';
import './Bulle.css';

/**
 * Une ligne de la Liste (chantier `revue-design`, ticket 03, maquette `design/liste.html`,
 * décisions d'Igor du 2026-10-03, « La Liste » 2 et 3).
 *
 * De gauche à droite : ton rang (le seul chiffre qui soit un jugement), le chevron d'une étape
 * qui déplie ses tâches sur place, le rond de son état (une idée en carré), le titre, et ce
 * que la ligne dit d'elle-même. Les tâches dépliées se posent en retrait, un cran par niveau.
 *
 * **Les gestes de la Liste** (décision 2, retour de test n° 6) : un clic sur la ligne ouvre sa
 * fiche en panneau, la Liste reste là et la ligne ouverte est surlignée ; un double-clic entre
 * dedans (on quitte la Liste), comme « Creuser » dans la fiche. Le rond change l'état sans
 * quitter la feuille : sur ordinateur il fait le tour des états, au téléphone il coche et défait
 * (`rondQuiCoche.ts`). Aucun glisser (principe 8).
 *
 * **Au téléphone, un seul geste** (chantier `telephone-cadre`, ticket 04, `ligneAuPouce.ts`) :
 * une tâche (rien dedans) a son rond, et la toucher ouvre sa fiche ; une étape (quelque chose
 * dedans, même fait) n'a pas de rond mais un › au bout, et la toucher y entre. L'appui long ouvre
 * la fiche des deux. Ni chevron ni dépliage sur place : on entre, un niveau à la fois.
 */
export function Bulle({
  bulle,
  niveau,
  dit,
  deplie,
  ouverte,
  rangEnAttente,
  enHierarchisation,
  auPouce,
  onEntrer,
  onFiche,
  onRond,
  onDeplier,
  onDesigner,
}: {
  bulle: BulleEnrichie;
  /** 1 pour une bulle de la feuille, 2 pour une tâche dépliée dessous, et ainsi de suite. */
  niveau: number;
  /** Ce que la ligne dit d'elle-même, à droite (`ceQueDitLaLigne`). */
  dit: { texte: string; monde: boolean };
  deplie: boolean;
  /** Sa fiche est ouverte en panneau : la ligne est surlignée. */
  ouverte: boolean;
  /** Le numéro posé pendant qu'on hiérarchise, avant enregistrement. */
  rangEnAttente: number | null;
  enHierarchisation: boolean;
  /** Au téléphone, le rond coche ; sur ordinateur, il fait tourner les états. */
  auPouce: boolean;
  onEntrer: (id: string) => void;
  onFiche: (id: string) => void;
  /** Le rond touché : la feuille fait le geste que `gesteDuRond` lui dit. */
  onRond: (bulle: BulleEnrichie) => void;
  onDeplier: (id: string) => void;
  onDesigner: (id: string) => void;
}) {
  const gestesDeLOrdinateur = useDeuxGestes(
    () => onFiche(bulle.id),
    () => onEntrer(bulle.id)
  );
  const auDoigt = gestesDeLaLigneAuPouce(bulle);
  const gestes = auPouce
    ? gestesDuToucher(
        () => (auDoigt.toucher === 'entrer' ? onEntrer(bulle.id) : onFiche(bulle.id)),
        () => onFiche(bulle.id)
      )
    : gestesDeLOrdinateur;

  const etat = bulle.etat;
  const enReflexion = etat === 'sans_objet';
  const jamaisOuverte = bulle.jamaisOuverte && etat === 'a_faire';

  /*
   * Pendant qu'on hiérarchise (la touche h, ou « Hiérarchiser » sous ⚙ en Carte et en
   * Hiérarchie), la ligne entière devient une cible : on désigne dans l'ordre. Seules les
   * bulles de la feuille se désignent ; la Liste ne montre alors qu'elles.
   */
  if (enHierarchisation) {
    const detail = ligneDeProprietes(bulle);
    return (
      <li
        className={`bulle bulle--designable${rangEnAttente !== null ? ' bulle--numerotee' : ''}`}
        data-tuto="bulle-liste"
        data-bulle={bulle.id}
      >
        <button type="button" className="bulle-designer" onClick={() => onDesigner(bulle.id)}>
          <span className="bulle-numero">{rangEnAttente ?? ''}</span>
          <span className="bulle-corps">
            <span className="bulle-titre">{bulle.titre}</span>
            {detail.length > 0 && <span className="bulle-detail">{detail.join(' · ')}</span>}
          </span>
        </button>
      </li>
    );
  }

  const classes = [
    'bulle',
    etat === 'fait' ? 'bulle--eteinte' : '',
    enReflexion ? 'bulle--idee' : '',
    ouverte ? 'bulle--ouverte' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li
      className={classes}
      data-tuto="bulle-liste"
      data-bulle={bulle.id}
      style={{ ['--retrait' as string]: niveau - 1 }}
    >
      {/* Ton rang : la colonne existe toujours, pour que les titres s'alignent. */}
      {/* Dessous, une tâche dépliée a son rang dans l'étape : il se lit en entrant dedans. */}
      <span
        className="bulle-rang"
        aria-label={niveau === 1 && bulle.rang ? `ton rang : ${bulle.rang}` : undefined}
      >
        {niveau === 1 ? (bulle.rang ?? '') : ''}
      </span>

      {/* Au téléphone, pas de chevron : on entre dans l'étape au lieu de la déplier. */}
      {auPouce ? null : bulle.nbEnfants > 0 ? (
        <button
          type="button"
          className="bulle-deplier"
          onClick={() => onDeplier(bulle.id)}
          aria-expanded={deplie}
          aria-label={deplie ? REPLIER_SES_TACHES : DEPLIER_SES_TACHES}
          title={deplie ? REPLIER_SES_TACHES : DEPLIER_SES_TACHES}
        >
          <ChevronDeLEtape deplie={deplie} />
        </button>
      ) : (
        <span className="bulle-sans-deplier" aria-hidden="true" />
      )}

      {/* Au téléphone, le rond est celui d'une tâche : une étape a son › au bout. */}
      {(!auPouce || auDoigt.rond) && (
        <button
          type="button"
          className="bulle-pastille"
          onClick={() => onRond(bulle)}
          aria-label={`état : ${auMilieu(jamaisOuverte ? RIEN_DEDANS : MOTS_ETAT[etat])}`}
          title={titreDuRond(etat, auPouce)}
        >
          {enReflexion ? (
            <CarreDIdee />
          ) : jamaisOuverte ? (
            <PastilleJamaisOuverte />
          ) : (
            <PastilleEtat etat={etat} taille={19} />
          )}
        </button>
      )}

      <button
        type="button"
        className="bulle-corps"
        aria-current={ouverte ? 'true' : undefined}
        {...gestes}
        title={
          !auPouce ? GESTES_DE_LA_LISTE : auDoigt.entrer ? GESTES_D_UNE_ETAPE_AU_POUCE : GESTES_D_UNE_TACHE_AU_POUCE
        }
      >
        {auPouce ? (
          <>
            <span className="bulle-lignes">
              <span className="bulle-titre">{bulle.titre}</span>
              {dit.texte && (
                <span className={`bulle-dit${dit.monde ? ' bulle-dit--monde' : ''}`}>{dit.texte}</span>
              )}
            </span>
            {auDoigt.entrer && (
              <span className="bulle-entrer" aria-hidden="true">
                ›
              </span>
            )}
          </>
        ) : (
          <>
            <span className="bulle-titre">{bulle.titre}</span>
            {dit.texte && (
              <span className={`bulle-dit${dit.monde ? ' bulle-dit--monde' : ''}`}>{dit.texte}</span>
            )}
          </>
        )}
      </button>
    </li>
  );
}
